import { Box, Typography, CircularProgress, Paper, Fade } from "@mui/material";
import type { LoadingProgress } from "../../../store/selection";

interface ProgressLoaderProps {
  progress: LoadingProgress | null;
  fallbackMessage?: string;
  isFetching?: boolean;
}

export default function ProgressLoader({ progress, fallbackMessage = "Loading...", isFetching = false }: ProgressLoaderProps) {
  // Only show if we're actually fetching or have progress data to show
  const show = isFetching || !!progress;
  if (!show) return null;

  const step = progress?.step || fallbackMessage;
  const current = progress?.current || 0;
  const total = progress?.total || 0;
  const percentage = total > 0 ? (current / total) * 100 : 0;

  return (
    <Fade in={show}>
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 240,
          height: 50,
          p: 2.5,
          pb: 3,
          gap: 1.5,
          borderRadius: "0.275rem",
          overflow: "hidden",
          background: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04)",
          textAlign: "center",
          pointerEvents: "auto" // Ensure it's interactive if needed
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={16} thickness={6} sx={{ color: "grey.800" }} />
          <Typography
            variant='body2'
            sx={{
              fontWeight: 500,
              color: "grey.700",
              letterSpacing: "0.01em"
            }}
          >
            {step} {total > 0 && `(${current}/${total})`}
          </Typography>
        </Box>

        {/* Dynamic Progress Bar at the absolute bottom */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 4,
            bgcolor: "grey.200", // Track
            zIndex: 1
          }}
        >
          <Box
            sx={{
              width: `${Math.min(percentage, 100)}%`,
              height: "100%",
              bgcolor: "primary.main", // Progress bar color
              transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              borderRadius: "0 2px 2px 0",
              boxShadow: percentage > 0 ? "0 0 8px rgba(0,0,0,0.2)" : "none"
            }}
          />
        </Box>
      </Paper>
    </Fade>
  );
}
